/**
 * What a dossier still misses (a dossier status change's `missing_items`, the review sheet's
 * `missing`) in words. The kinds are the ones the dossier's evaluation writes (identity_dossiers
 * `evaluate` and its file rules; tests/dossier-missing-items.test.mjs reads them from the Rust
 * sources), each read by the labels the console already has:
 * - `subject`, `identity_verification`, `manual_review`, `document:evidence`: `accounts:dossier.missingItem`;
 * - `document:<document type or purpose>`, `document_type:<catalogue type>`: the document's name
 *   (`accounts:enum.purpose`, else `accounts:enum.documentType`);
 * - `consent:<type>`: `accounts:dossier.consents` and `accounts:enum.consentType`;
 * - `hstm:<types>:<signed status>` (types joined by `+`, alternatives by `|`): the VNNIC files as
 *   the review sheet's VNNIC panel names them (`domains:dossier.fileTypes`, `…signedStatus`);
 * - `hstm:error:<text>`: why the dossier cannot be sent to VNNIC, a reason the HSTM plan gives in
 *   English, as the domain page's dossier panel names it (`errorCode`, `domainPage:dossierPanel.blocker`);
 *   a text it does not name is kept as the technical detail.
 * Anything else is shown as it is.
 *
 * A plain function with a lookup (`ns:key`) so tests run in Node.
 */

import { errorCode } from './dossierPanel.ts';

export type MissingItemLookup = (key: string) => string | undefined;

/** A missing item in words; `detail` is the server's own text when the console cannot name it. */
export interface MissingItemText {
  text: string;
  detail: string | null;
}

export function missingItemWith(item: unknown, lookup: MissingItemLookup): MissingItemText {
  if (typeof item !== 'string') return { text: '', detail: null };
  const colon = item.indexOf(':');
  const kind = colon < 0 ? item : item.slice(0, colon);
  const value = item.slice(colon + 1);
  const label = (key: string, text: string) => {
    const l = lookup(key);
    return l ? `${l}: ${text}` : item;
  };
  const words = (text: string, detail: string | null = null): MissingItemText => ({ text, detail });
  const doc = (code: string) => lookup(`accounts:enum.purpose.${code}`) ?? lookup(`accounts:enum.documentType.${code}`) ?? code;
  if (colon < 0 || item === 'document:evidence') return words(lookup(`accounts:dossier.missingItem.${kind === 'document' ? 'evidence' : kind}`) ?? item);
  if (kind === 'document' || kind === 'document_type') return words(doc(value));
  if (kind === 'consent') return words(label('accounts:dossier.consents', lookup(`accounts:enum.consentType.${value}`) ?? value));
  if (kind === 'hstm' && value.startsWith('error:')) {
    const text = value.slice('error:'.length);
    const code = errorCode(text);
    const named = lookup(`domainPage:dossierPanel.blocker.${code}`);
    return named ? words(label('domains:dossier.hstmErrors', named), code === 'OTHER' ? text : null) : words(label('domains:dossier.hstmErrors', text));
  }
  const files = kind === 'hstm' ? /^([\d+|]+):(\d+)$/.exec(value) : null;
  if (!files) return words(item);
  const type = (n: string) => lookup(`domains:dossier.fileTypes.${n}`) ?? `#${n}`;
  const alternatives = (files[1] ?? '').split('|').map((a) => a.split('+').map(type).join(' + '));
  return words(label('domains:dossier.vnnicFiles', `${alternatives.join(' / ')} (${lookup(`domains:dossier.signedStatus.${files[2]}`) ?? files[2]})`));
}
